import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, X, ArrowLeft, Eye, Check, Calendar } from 'lucide-react';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
const TODAY_DATE = new Date().toISOString().split('T')[0];


function TabelaPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View states
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'detail'
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    other_details: ''
  });

  // List pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Filter states
  const [filters, setFilters] = useState({
    name: '',
    address: '',
    other_details: ''
  });

  // Detail view data
  const [milkData, setMilkData] = useState([]);
  const [summary, setSummary] = useState({});
  const [milkPagination, setMilkPagination] = useState({});
  const [milkCurrentPage, setMilkCurrentPage] = useState(1);
  const [dateFilters, setDateFilters] = useState({
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadTabelas();
  }, []);

  const loadTabelas = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTabela();
      setData(response.data || []);
      setError(null);
    } catch (error) {
      setError(error.message);
      console.error('Error loading tabelas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTabelaMilkCollection = async (tabelaId, page = 1, filters = {}) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 50,
        ...filters
      };
      
      const response = await apiService.getTabelaMilkCollection(tabelaId, params);
      setMilkData(response.data || []);
      setSummary(response.summary || {});
      setMilkPagination(response.pagination || {});
      setError(null);
    } catch (error) {
      setError(error.message);
      console.error('Error loading milk collection:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data
  const filteredData = data.filter(item => {
    return (
      (filters.name === '' || item.name.toLowerCase().includes(filters.name.toLowerCase())) &&
      (filters.address === '' || item.address.toLowerCase().includes(filters.address.toLowerCase())) &&
      (filters.other_details === '' || item.other_details.toLowerCase().includes(filters.other_details.toLowerCase()))
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', address: '', other_details: '' });
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      address: item.address,
      other_details: item.other_details
    });
    setCurrentView('form');
  };

  const handleView = (item) => {
    setViewingItem(item);
    setCurrentView('detail');
    setMilkCurrentPage(1);
    setDateFilters({ start_date: '', end_date: '' });
    loadTabelaMilkCollection(item.tabela_id, 1);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tabela?')) {
      try {
        await apiService.deleteTabela(id);
        await loadTabelas();
        
        const newTotalPages = Math.ceil((filteredData.length - 1) / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
      } catch (error) {
        toast.error('Error deleting tabela: ' + error.message);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.address.trim()) {
      toast.warning('Please fill in Name and Address');
      return;
    }

    try {
      setLoading(true);
      if (editingItem) {
        await apiService.updateTabela(editingItem.tabela_id, formData);
      } else {
        await apiService.createTabela(formData);
      }
      
      await loadTabelas();
      setCurrentView('list');
      setFormData({ name: '', address: '', other_details: '' });
      setEditingItem(null);
    } catch (error) {
      toast.error('Error saving tabela: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      address: '',
      other_details: ''
    });
  };

  const handleCancel = () => {
    setCurrentView('list');
    setFormData({ name: '', address: '', other_details: '' });
    setEditingItem(null);
    setViewingItem(null);
    setError(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleMilkPageChange = (page) => {
    setMilkCurrentPage(page);
    loadTabelaMilkCollection(viewingItem.tabela_id, page, dateFilters);
  };

  const handleDateFilterChange = (e) => {
    setDateFilters({
      ...dateFilters,
      [e.target.name]: e.target.value
    });
  };

  const applyDateFilters = () => {
    setMilkCurrentPage(1);
    loadTabelaMilkCollection(viewingItem.tabela_id, 1, dateFilters);
  };

  const clearDateFilters = () => {
    setDateFilters({ start_date: '', end_date: '' });
    setMilkCurrentPage(1);
    loadTabelaMilkCollection(viewingItem.tabela_id, 1);
  };

  if (loading && data.length === 0) {
    return (
      <div className="app-container">
        <Header />
        <div className="main-content">
          <Sidebar />
          <div className="page-content">
            <div className="loading">Loading tabelas...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error && data.length === 0) {
    return (
      <div className="app-container">
        <Header />
        <div className="main-content">
          <Sidebar />
          <div className="page-content">
            <div className="error-message">
              Error loading tabelas: {error}
              <button className="btn btn-primary" onClick={loadTabelas}>Retry</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Detail View
  if (currentView === 'detail') {
    const tabela = viewingItem;
    
    return (
      <div className="app-container">
        <Header />
        <div className="main-content">
          <Sidebar />
          <div className="page-content">
            <div className="page-header">
              <div className="header-left">
                <button className="btn btn-secondary" onClick={handleCancel}>
                  <ArrowLeft size={18} />
                  Back to Tabela List
                </button>
                <div style={{ marginTop: '1rem' }}>
                  <h1 className="page-title">Tabela Details - {tabela.name}</h1>
                </div>
              </div>
              <button className="btn btn-primary" onClick={() => handleEdit(tabela)}>
                <Edit size={18} /> Edit Tabela
              </button>
            </div>
            
            {error && (
              <div className="alert alert-error">
                <p>{error}</p>
              </div>
            )}
            
            <div className="form-row">
              <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                  <h3 className="card-title">Tabela Information</h3>
                </div>
                <div className="form-group">
                    <label className="form-label">Tabela ID</label>
                    <div style={{ fontSize: '1.05rem', fontWeight: 500 }}>#{tabela.tabela_id}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">Name</label>
                    <div style={{ fontSize: '1.05rem' }}>{tabela.name}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">Address</label>
                    <div style={{ fontSize: '1.05rem' }}>{tabela.address}</div>
                </div>
                <div className="form-group">
                    <label className="form-label">Other Details</label>
                    <div style={{ fontSize: '1.05rem' }}>{tabela.other_details}</div>
                </div>
              </div>

              <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                  <h3 className="card-title">Summary Statistics</h3>
                </div>
                <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                  <div className="stat-box" style={{ background: '#eff6ff', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#1d4ed8' }}>Total Collection</label>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e40af' }}>{summary.total_collection?.toFixed(2) || 0} L</span>
                  </div>
                  <div className="stat-box" style={{ background: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#15803d' }}>Active Animals</label>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#166534' }}>{summary.active_animals || 0}</span>
                  </div>
                  <div className="stat-box" style={{ background: '#fef3c7', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#b45309' }}>Total Khatas</label>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#92400e' }}>{summary.total_khatas || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Daily Milk Collection</h3>
                  <div className="filter-bar" style={{ marginBottom: 0, marginTop: '1rem' }}>
                    <div className="filter-group">
                      <input
                        type="date" max={TODAY_DATE}
                        name="start_date"
                        className="form-input"
                        value={dateFilters.start_date}
                        onChange={handleDateFilterChange}
                      />
                    </div>
                    <div className="filter-group">
                      <input
                        type="date" max={TODAY_DATE}
                        name="end_date"
                        className="form-input"
                        value={dateFilters.end_date}
                        onChange={handleDateFilterChange}
                      />
                    </div>
                    <button className="btn btn-primary" onClick={applyDateFilters}>
                      <Calendar size={16} /> Apply
                    </button>
                    <button className="btn btn-secondary" onClick={clearDateFilters}>
                      Clear
                    </button>
                  </div>
                </div>
                
                <div className="table-container" style={{ margin: 0, border: 'none', boxShadow: 'none' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Cow Morning (L)</th>
                        <th>Cow Night (L)</th>
                        <th>Cow Total (L)</th>
                        <th>Buffalo Morning (L)</th>
                        <th>Buffalo Night (L)</th>
                        <th>Buffalo Total (L)</th>
                        <th>Daily Total (L)</th>
                        <th>Animals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {milkData.map((item, index) => (
                        <tr key={index}>
                          <td data-label="Date">{new Date(item.date).toLocaleDateString()}</td>
                          <td data-label="Cow Morning (L)">{item.cow_morning.toFixed(2)}</td>
                          <td data-label="Cow Night (L)">{item.cow_night.toFixed(2)}</td>
                          <td data-label="Cow Total (L)"><strong>{item.cow_total.toFixed(2)}</strong></td>
                          <td data-label="Buffalo Morning (L)">{item.buffalo_morning.toFixed(2)}</td>
                          <td data-label="Buffalo Night (L)">{item.buffalo_night.toFixed(2)}</td>
                          <td data-label="Buffalo Total (L)"><strong>{item.buffalo_total.toFixed(2)}</strong></td>
                          <td data-label="Daily Total (L)"><strong style={{color: 'var(--primary)'}}>{item.total_milk.toFixed(2)}</strong></td>
                          <td data-label="Animals">
                            <span title={`Cows: ${item.cow_count}, Buffalos: ${item.buffalo_count}`}>
                              {item.total_animals}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {milkData.length === 0 && !loading && (
                    <div className="no-data">
                      <p>No milk collection data found for the selected date range</p>
                    </div>
                  )}
                </div>
                
                {milkPagination.pages > 1 && (
                  <div className="pagination">
                    <button 
                      className="pagination-btn"
                      onClick={() => handleMilkPageChange(milkCurrentPage - 1)}
                      disabled={milkCurrentPage === 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    
                    {[...Array(milkPagination.pages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`pagination-btn ${milkCurrentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => handleMilkPageChange(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handleMilkPageChange(milkCurrentPage + 1)}
                      disabled={milkCurrentPage === milkPagination.pages}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
          </div>
        </div>
      </div>
    );
  }

  // Form View
  if (currentView === 'form') {
    return (
      <div className="app-container">
        <Header />
        <div className="main-content">
          <Sidebar />
          <div className="page-content">
            <div className="page-header">
              <div className="header-left">
                <button className="btn btn-secondary" onClick={handleCancel}>
                  <ArrowLeft size={18} />
                  Back to Tabela List
                </button>
                <div style={{ marginTop: '1rem' }}>
                  <h1 className="page-title">{editingItem ? 'Edit Tabela' : 'Add New Tabela'}</h1>
                </div>
              </div>
            </div>
            
            {error && (
              <div className="alert alert-error">
                <p>{error}</p>
              </div>
            )}
            
            <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Enter tabela name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="address" className="form-label">Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    className="form-input"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Enter address"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="other_details" className="form-label">Other Details</label>
                  <textarea
                    id="other_details"
                    name="other_details"
                    className="form-textarea"
                    value={formData.other_details}
                    onChange={handleInputChange}
                    rows="4"
                    disabled={loading}
                    placeholder="Enter other details"
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                    <X size={16} />
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                    <Check size={16} />
                    {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          <div className="page-header">
            <h1 className="page-title">Tabela Management</h1>
            <button className="btn btn-primary" onClick={handleAdd}>
              <Plus size={18} />
              Add Tabela
            </button>
          </div>
          
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Other Details</th>
                  <th>Actions</th>
                </tr>
                {/* Filter row */}
                <tr className="filter-row">
                  <th>-</th>
                  <th>
                    <input
                      type="text"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                      placeholder="Filter by name"
                      className="form-input"
                      style={{ padding: '0.4rem' }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="address"
                      value={filters.address}
                      onChange={handleFilterChange}
                      placeholder="Filter by address"
                      className="form-input"
                      style={{ padding: '0.4rem' }}
                    />
                  </th>
                  <th>
                   -
                  </th>
                  <th>
                    <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map(item => (
                  <tr key={item.tabela_id} className="clickable-row">
                    <td data-label="ID" onClick={() => handleView(item)}>#{item.tabela_id}</td>
                    <td data-label="Name" onClick={() => handleView(item)} style={{ fontWeight: 500 }}>{item.name}</td>
                    <td data-label="Address" onClick={() => handleView(item)}>{item.address}</td>
                    <td data-label="Other Details" onClick={() => handleView(item)}>{item.other_details}</td>
                    <td data-label="Actions">
                      <div className="actions-cell">
                        <button 
                          className="btn-icon"
                          onClick={() => handleView(item)}
                          disabled={loading}
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          className="btn-icon"
                          style={{ color: 'var(--error-color)' }}
                          onClick={() => handleDelete(item.tabela_id)}
                          disabled={loading}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {currentItems.length === 0 && !loading && (
              <div className="no-data">
                <p>No tabelas found</p>
                {(filters.name || filters.address || filters.other_details) && (
                  <button className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
                )}
              </div>
            )}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            <p>Showing {currentItems.length} of {filteredData.length} tabelas</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TabelaPage;